<template>
  <section class="verify-section">
    <div class="verify-card">
      <h2>{{ title }}</h2>
      <p>{{ message }}</p>

      <!-- Başarılı doğrulama sonrası girişe yönlendir -->
      <router-link v-if="status === 'success'" to="/login" class="btn">
        {{ $t('verifyEmail.goLogin') }}
      </router-link>

      <!-- Expired ise tekrar gönderme -->
      <div v-if="status === 'expired'" class="resend-container">
        <p class="info">{{ $t('verifyEmail.didntWork') }}</p>
        <button @click="resendVerification" class="btn small" :disabled="sending">
          {{ sending ? $t('verifyEmail.sending') : $t('verifyEmail.resend') }}
        </button>
        <p v-if="resendMessage" class="success">{{ resendMessage }}</p>
        <p v-if="resendError" class="error">{{ resendError }}</p>
        <p v-if="status === 'success'" class="info">
          {{ $t('verifyEmail.redirecting') }}
        </p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router'
import { computed, ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const token = route.query.token
const email = route.query.email
const pageStatus = ref('')

const status = computed(() => route.query.status || pageStatus.value || '')

const title = computed(() =>
  status.value === 'success'
    ? t('verifyEmail.successTitle')
    : status.value === 'expired'
    ? t('verifyEmail.expiredTitle')
    : t('verifyEmail.defaultTitle')
)

const message = computed(() =>
  status.value === 'success'
    ? t('verifyEmail.successMessage')
    : status.value === 'expired'
    ? t('verifyEmail.expiredMessage')
    : t('verifyEmail.defaultMessage')
)

const resendMessage = ref('')
const resendError = ref('')
const sending = ref(false)

async function resendVerification() {
  resendMessage.value = ''
  resendError.value = ''
  sending.value = true
  try {
    const res = await axios.post('/resend-verification', { email })
    resendMessage.value = res.data.message
  } catch (err) {
    resendError.value = err.response?.data?.message || t('verifyEmail.error')
  } finally {
    sending.value = false
  }
}
onMounted(async () => {
  if (!route.query.status && token) {
    try {
      const res = await axios.post('/auth/verify-email', { token })
      if (res.data.success) {
        pageStatus.value = 'success'
        router.replace({ path: '/verify', query: { status: 'success', email: res.data.email } })
      } else {
        pageStatus.value = 'expired'
        router.replace({ path: '/verify', query: { status: 'expired', email: res.data.email } })
      }
    } catch (err) {
      pageStatus.value = 'expired'
      router.replace({ path: '/verify', query: { status: 'expired', email } })
    }
  }

  // ✅ Otomatik yönlendirme
  if (status.value === 'success') {
    setTimeout(() => {
      router.push('/login')
    }, 3000)
  }
})
</script>



<style scoped>
.verify-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 40px 20px;
}
.verify-card {
  background: #fff;
  padding: 32px;
  border-radius: 12px;
  max-width: 400px;
  width: 100%;
  text-align: center;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
}
.btn {
  display: inline-block;
  margin-top: 20px;
  padding: 10px 20px;
  background-color: #0c2d57;
  color: white;
  border-radius: 8px;
  text-decoration: none;
  cursor: pointer;
}
.btn.small {
  padding: 8px 16px;
  font-size: 14px;
}
.success {
  color: green;
  margin-top: 12px;
}
.error {
  color: red;
  margin-top: 12px;
}
.resend-container {
  margin-top: 20px;
}
.info {
  font-size: 14px;
  margin-bottom: 8px;
}
@media (max-width: 600px) {
  .verify-card {
    padding: 24px 16px;
    border-radius: 10px;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.06);
  }

  .btn {
    width: 100%;
    font-size: 15px;
    padding: 10px;
  }

  .btn.small {
    padding: 8px;
    font-size: 14px;
  }

  .verify-section {
    padding: 30px 12px;
  }

  .info,
  .success,
  .error {
    font-size: 13px;
    text-align: left;
  }

  h2 {
    font-size: 20px;
  }

  p {
    font-size: 14px;
  }
}
</style>
